import { useEffect, useRef } from 'react';
import { Text } from './Text.jsx';
import { cn } from '../lib/cn.js';

const SIZE = {
  sm: 'max-w-md',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
};

let bodyScrollLockCount = 0;
let previousBodyOverflow = '';

function lockBodyScroll() {
  if (typeof document === 'undefined') return () => {};
  if (bodyScrollLockCount === 0) {
    previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  }
  bodyScrollLockCount += 1;
  return () => {
    bodyScrollLockCount = Math.max(0, bodyScrollLockCount - 1);
    if (bodyScrollLockCount === 0) {
      document.body.style.overflow = previousBodyOverflow;
      previousBodyOverflow = '';
    }
  };
}

export function Modal({ open, onClose, title, ariaLabel, children, size = 'md' }) {
  const ref = useRef(null);

  function handleBackdropClick(event) {
    if (event.target !== event.currentTarget) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const clickedOutside = event.clientX < rect.left
      || event.clientX > rect.right
      || event.clientY < rect.top
      || event.clientY > rect.bottom;
    if (clickedOutside) onClose?.();
  }

  function handleCancel(event) {
    event.preventDefault();
    onClose?.();
  }

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return undefined;
    if (open && !dialog.open) {
      if (typeof dialog.showModal === 'function') {
        dialog.showModal();
      } else {
        dialog.setAttribute('open', '');
      }
    }
    if (!open && dialog.open) {
      if (typeof dialog.close === 'function') {
        dialog.close();
      } else {
        dialog.removeAttribute('open');
      }
    }
    return undefined;
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    return lockBodyScroll();
  }, [open]);

  return (
    <dialog
      ref={ref}
      onClose={onClose}
      onCancel={handleCancel}
      onClick={handleBackdropClick}
      aria-label={ariaLabel}
      className={cn(
        'app-dialog w-[calc(100vw-2rem)] rounded-card border border-line bg-paper text-ink p-4 sm:w-full sm:p-6',
        SIZE[size] ?? SIZE.md,
      )}
    >
      {title && <Text variant="h3" className="mb-4">{title}</Text>}
      {children}
    </dialog>
  );
}
